import { selector, selectorFamily, waitForAll } from 'recoil';
import { analysis, type DetailedProxyMetadata, type SubjectRangeAnalysis } from 'venn-api';
import { createProxyMetadataKey, extractHoldingsProxy, hasFeatureSelector, VennQueryClient } from 'venn-utils';
import { convertStudioSubjectToApiSubject } from './utils';
import { compact, flatMap, isEmpty, mapValues, keyBy, omit, uniqBy, zipObject, some } from 'lodash';
import { allUniqViewSubjectsAndBenchmarks } from './configuration/allViewSubjects';
import { allBlockIdsState } from './grid';
import { blockBenchmarkSubjects, blockLimitedRequestSubjects } from './configuration/blockConfig';
import { requestSubjects } from './configuration/subjects';
import type { BlockId, StudioRequestSubject } from './types';
import { isReportState } from './configuration/view';
import { isReturnsBasedBlockState, isHoldingsBasedBlockState, blocksOnPage } from './configuration/print';

export type ProxyMetadataAndRangeBySubject = {
  proxiesMetadata: { [subjectId: string]: DetailedProxyMetadata[] | null | undefined };
  rangeAnalysis: { [subjectId: string]: SubjectRangeAnalysis | null | undefined };
};

/** Returns ALL proxy infos for the entire view, regardless of if the block it comes from is returns-based. */
export const allProxyInfoStateForExport = selector<ProxyMetadataAndRangeBySubject>({
  key: 'allProxyInfoStateForExport',
  get: async ({ get }) => {
    const subjects = get(allUniqViewSubjectsAndBenchmarks);

    if (subjects.length === 0) {
      return { proxiesMetadata: {}, rangeAnalysis: {} };
    }

    const reqSubjects = get(requestSubjects(subjects));
    const apiSubjects = reqSubjects.map((subject) => convertStudioSubjectToApiSubject(subject, 'PRIMARY', true));
    const queryKey = createProxyMetadataKey(apiSubjects);

    const queryClient = VennQueryClient.getInstance();
    await queryClient.invalidateQueries(queryKey);
    const result = await queryClient.fetchQuery(queryKey, () =>
      analysis({
        analyses: [
          {
            analysisType: 'DETAILED_PROXY_METADATA',
            relative: false,
            scenarios: [],
          },
          {
            analysisType: 'RANGE_ANALYSIS',
            relative: false,
            scenarios: [],
          },
        ],
        subjects: apiSubjects,
      }),
    );

    const subjectIds = apiSubjects.map((subject) => subject.id);
    const proxyMetadataBySubject = zipObject(subjectIds, result.content.analyses[0]!.detailedProxyMetadata);
    const rangeAnalysisBySubject = zipObject(
      subjectIds,
      result.content.analyses[1]?.rangeAnalysis?.rangeAnalyses ?? [],
    );

    return {
      proxiesMetadata: proxyMetadataBySubject,
      rangeAnalysis: rangeAnalysisBySubject,
    };
  },
});

/** Returns ALL proxy metadata for the entire view, regardless of if the block it comes from is returns-based. */
export const allProxyInfoState = selector<{ [subjectId: string]: DetailedProxyMetadata[] | null | undefined }>({
  key: 'allProxyInfo',
  get: ({ get }) => {
    if (!get(isReportState)) {
      return {};
    }

    return get(allProxyInfoStateForExport).proxiesMetadata;
  },
});

export const flattenedProxyMapState = selector<{ [fundId: string]: DetailedProxyMetadata | null | undefined }>({
  key: 'flattenedProxyMapState',
  get: ({ get }) => {
    const allProxiesInfo = get(allProxyInfoState);
    const flattenedProxyInfo = Object.values(allProxiesInfo)
      .flat()
      .filter((meta) => meta);
    return omit(
      {
        ...keyBy(flattenedProxyInfo, 'fundId'),
        ...keyBy(flattenedProxyInfo, 'parentId'),
      },
      'undefined',
      'null',
    );
  },
});

export type UsedProxyWithParents = DetailedProxyMetadata &
  Pick<SubjectRangeAnalysis, 'proxyInfos' | 'extrapolateStartDate' | 'extrapolateEndDate'> & {
    parentSubjectNames: string[];
  };

export const hasProxyState = selectorFamily<boolean, string>({
  key: 'hasProxyState',
  get:
    (subjectId) =>
    ({ get }) =>
      !isEmpty(get(allProxyInfoState)[subjectId]),
});

/**
 * Returns info about all actively in-use proxies in the view for XLS Export.
 *
 * Excludes non-returns based blocks, because proxies don't affect non-returns based analyses.
 */
export const investmentProxiesStateForExport = selector<UsedProxyWithParents[]>({
  key: 'investmentProxiesStateForExport',
  get: ({ get }) => {
    const holding_proxy_ff = get(hasFeatureSelector('holding_proxy_ff'));
    const blockIds = get(allBlockIdsState);
    const allProxiedInvestments: UsedProxyWithParents[] = get(investmentUsedProxiesStateForExport(blockIds));

    return holding_proxy_ff ? allProxiedInvestments : allProxiedInvestments.filter((i) => i.proxyVariant === 'RETURN');
  },
});

/**
 * Returns info about all actively in-use proxies in the view.
 *
 * Excludes non-returns based blocks, because proxies don't affect non-returns based analyses.
 */
export const investmentProxiesState = selector<UsedProxyWithParents[]>({
  key: 'investmentProxiesState',
  get: ({ get }) => {
    if (!get(isReportState)) {
      return [];
    }

    return get(investmentProxiesStateForExport);
  },
});

/**
 * Sorts proxies by fundName and groups them by fundId.
 */
export const getSortedUsedProxies = (usedProxies: UsedProxyWithParents[]) => {
  const sorted = [...usedProxies].sort((a, b) => a.fundName.localeCompare(b.fundName));

  return sorted.reduce<{ [fundId: string]: UsedProxyWithParents[] }>((acc, proxy) => {
    (acc[proxy.fundId] ||= []).push(proxy);
    return acc;
  }, {});
};

/**
 * Returns true if there are any proxies actively in-use on the provided page index.
 * Includes both RETURN and HOLDING proxies based on block types.
 */
export const hasUsedProxiesOnPageState = selectorFamily<boolean, number>({
  key: 'hasUsedProxiesOnPageState',
  get:
    (pageNumber) =>
    ({ get }) => {
      const blockIds = get(blocksOnPage(pageNumber));
      const usedProxies = get(blockSubjectProxiesState(blockIds));

      return Object.values(usedProxies).some(({ proxies }) => !isEmpty(proxies));
    },
});

/**
 * Includes both RETURN and HOLDING proxies based on block types.
 * Deduplicates proxies by fundId, proxyId, and proxyVariant, collecting all parent subject names.
 */
const groupAndMergeProxies = (subjectIdToUsedProxies: {
  [subjectId: string]: {
    subject: StudioRequestSubject;
    proxies: DetailedProxyMetadata[];
    proxiesRangeAnalysis: SubjectRangeAnalysis[];
  };
}) => {
  // Deduplicate proxies by fundId and proxyId, collecting parent subject names
  const proxyMap = Object.values(subjectIdToUsedProxies).reduce<{
    [key: string]: UsedProxyWithParents & { parentNameSet: Set<string> };
  }>((acc, { subject, proxies, proxiesRangeAnalysis }) => {
    const proxyRangeByInvestmentId = keyBy(
      proxiesRangeAnalysis.filter(({ investmentId }) => investmentId),
      'investmentId',
    );

    proxies.forEach((meta) => {
      const key = `${meta.fundId}|${meta.proxyId}|${meta.proxyVariant}`;

      if (!acc[key]) {
        const proxyRangeAnalysis = proxyRangeByInvestmentId[meta.fundId];
        acc[key] = {
          ...meta,
          parentSubjectNames: [],
          parentNameSet: new Set(),
          proxyInfos: proxyRangeAnalysis?.proxyInfos ?? [],
          extrapolateStartDate: proxyRangeAnalysis?.extrapolateStartDate ?? 0,
          extrapolateEndDate: proxyRangeAnalysis?.extrapolateEndDate ?? 0,
        };
      }

      // Track portfolio and benchmark subjects as parent names
      if (subject.portfolio || subject.fund?.assetType === 'BENCHMARK') {
        acc[key].parentNameSet.add(subject.name);
      }
    });

    return acc;
  }, {});

  // Convert Sets to arrays and remove the temporary Set field
  return Object.values(proxyMap).map(({ parentNameSet, ...proxy }) => ({
    ...proxy,
    parentSubjectNames: Array.from(parentNameSet),
  }));
};

/**
 * Includes both RETURN and HOLDING proxies based on block types.
 * Deduplicates proxies by fundId and proxyId, collecting all parent subject names.
 */
const investmentUsedProxiesStateForExport = selectorFamily<UsedProxyWithParents[], BlockId[]>({
  key: 'investmentUsedProxiesStateForExport',
  get:
    (blockIds) =>
    ({ get }) => {
      const subjectIdToUsedProxies = get(blockSubjectProxiesStateForExport(blockIds));
      return groupAndMergeProxies(subjectIdToUsedProxies);
    },
});

/**
 * Returns a map of subject IDs to whether or not they have proxies relevant to this block type.
 * If a block subject has no proxies, then the subject ID may be omitted entirely.This is a side effect of
 * the way the lookup is implemented.
 * For returns-based blocks, checks for RETURN proxies.
 * For holdings-based blocks, checks for HOLDING proxies.
 */
export const blockSubjectHasProxyState = selectorFamily<{ [subjectId: string]: boolean | undefined }, BlockId>({
  key: 'blockSubjectHasProxyState',
  get:
    (blockId) =>
    ({ get }) => {
      const blockSubjectProxies = get(blockSubjectProxiesState([blockId]));
      return mapValues(blockSubjectProxies, ({ proxies }) => !isEmpty(proxies));
    },
});

/**
 * Returns proxies filtered by block type:
 * - Returns-based blocks only get RETURN proxies
 * - Holdings-based blocks only get HOLDING proxies
 */
export const blockSubjectProxiesStateForExport = selectorFamily<
  {
    [subjectId: string]: {
      subject: StudioRequestSubject;
      proxies: DetailedProxyMetadata[];
      proxiesRangeAnalysis: SubjectRangeAnalysis[];
    };
  },
  BlockId[]
>({
  key: 'blockSubjectProxiesStateForExport',
  get:
    (rawBlockIds) =>
    ({ get }) => {
      const blockSubjects = get(subjectsForBlockState(rawBlockIds));
      const proxyInfoBySubjectId = get(allProxyInfoStateForExport);

      const hasReturnsBasedBlock = some(rawBlockIds, (blockId) => get(isReturnsBasedBlockState(blockId)));
      const hasHoldingsBasedBlock = some(rawBlockIds, (blockId) => get(isHoldingsBasedBlockState(blockId)));

      return Object.fromEntries(
        compact(
          blockSubjects.map((subject) => {
            const subjectProxies = proxyInfoBySubjectId.proxiesMetadata[subject.id];
            const subjectProxiesRangeAnalysis = proxyInfoBySubjectId.rangeAnalysis[subject.id];
            const proxiesRangeAnalysis: SubjectRangeAnalysis[] =
              subjectProxiesRangeAnalysis?.rangeAnalyses ??
              (subjectProxiesRangeAnalysis ? [subjectProxiesRangeAnalysis] : []);

            if (!subjectProxies?.length) {
              return null;
            }

            const proxies = compact(
              flatMap(subjectProxies, (proxy) => {
                const result: DetailedProxyMetadata[] = [];

                if (hasReturnsBasedBlock && proxy.proxyVariant === 'RETURN') {
                  result.push(proxy);
                }

                if (hasHoldingsBasedBlock) {
                  const holdingsProxy = extractHoldingsProxy(proxy);
                  if (holdingsProxy) {
                    result.push(holdingsProxy);
                  }
                }

                return result;
              }),
            );

            return proxies.length > 0 ? [subject.id, { subject, proxies, proxiesRangeAnalysis }] : null;
          }),
        ),
      );
    },
});

/**
 * Returns proxies filtered by block type:
 * - Returns-based blocks only get RETURN proxies
 * - Holdings-based blocks only get HOLDING proxies
 */
export const blockSubjectProxiesState = selectorFamily<
  {
    [subjectId: string]: { subject: StudioRequestSubject; proxies: DetailedProxyMetadata[] };
  },
  BlockId[]
>({
  key: 'blockSubjectProxiesState',
  get:
    (rawBlockIds) =>
    ({ get }) => {
      if (!get(isReportState)) {
        return {};
      }
      return get(blockSubjectProxiesStateForExport(rawBlockIds));
    },
});

const subjectsForBlockState = selectorFamily<StudioRequestSubject[], BlockId[]>({
  key: 'subjectsForBlockState',
  get:
    (blockIds) =>
    ({ get }) => {
      const subjects = get(waitForAll(blockIds.map((id) => blockLimitedRequestSubjects(id)))).flat();
      const benchmarkSubjects = get(waitForAll(blockIds.map((id) => blockBenchmarkSubjects(id)))).flat();
      const benchmarkRequestSubjects = get(requestSubjects(benchmarkSubjects));
      return uniqBy([...subjects, ...benchmarkRequestSubjects], (subject) => subject.id);
    },
});
